import React from 'react';
import { BundleIssue, BundleReport, DeviceBundlePayload } from '../../../api/bundle';
import { IssueRow } from '../../../components/wizard/WizardShell';

// 设备向导第 ⑥ 步: 整包预检报告 + 发布选择 + 告警确认.
// 与材料向导同口径: 发布由提交人在这里拍板, 审批人只批准或驳回。

interface Props {
    payload: DeviceBundlePayload;
    patch: (p: Partial<DeviceBundlePayload['device']>) => void;
    report: BundleReport | null;
    checking: boolean;
    ack: boolean;
    setAck: (v: boolean) => void;
    canPublish: boolean;
    onGoto: (issue: BundleIssue) => void;
    done: { taskCode?: string; created?: number } | null;
}

function Group({
    title,
    tone,
    issues,
    onGoto,
}: {
    title: string;
    tone: string;
    issues: BundleIssue[];
    onGoto?: (i: BundleIssue) => void;
}) {
    if (issues.length === 0) return null;
    return (
        <div className="space-y-1.5">
            <div className={`text-xs font-semibold ${tone}`}>
                {title} ({issues.length})
            </div>
            {issues.map((i, k) => (
                <IssueRow key={`${i.code}-${i.path}-${k}`} issue={i} onGoto={onGoto} />
            ))}
        </div>
    );
}

function StepDeviceReview({
    payload,
    patch,
    report,
    checking,
    ack,
    setAck,
    canPublish,
    onGoto,
    done,
}: Props) {
    if (done) {
        return (
            <div className="card p-6 space-y-3 text-center">
                <div className="text-2xl">✅</div>
                <div className="text-sm font-semibold">
                    {done.taskCode ? '已提交审批' : '创建成功'}
                </div>
                {done.taskCode ? (
                    <div className="text-xs text-slate-500">
                        工单号 <span className="font-mono">{done.taskCode}</span> ·
                        管理员通过后整包一次落库
                    </div>
                ) : (
                    <div className="text-xs text-slate-500">
                        已创建 {done.created} 台设备
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold">
                    发布设置<span className="text-rose-500"> *</span>
                </div>
                <div className="space-y-2">
                    {(
                        [
                            [
                                true,
                                '创建后立即发布',
                                '审批通过即对 OpenAPI 可见, 下游能查到这台设备。',
                            ],
                            [
                                false,
                                '暂不发布',
                                '先建成未发布, 工况数据核对完再到设备页发布。',
                            ],
                        ] as [boolean, string, string][]
                    ).map(([val, label, hint]) => {
                        const disabled = val && !canPublish;
                        return (
                            <label
                                key={String(val)}
                                className={`flex items-start gap-2 text-xs rounded border px-3 py-2 ${
                                    disabled
                                        ? 'cursor-not-allowed border-slate-100 opacity-60'
                                        : 'cursor-pointer ' +
                                          (payload.device.publish === val
                                              ? 'border-brand-200 bg-brand-50'
                                              : 'border-slate-200 hover:bg-slate-50')
                                }`}
                            >
                                <input
                                    type="radio"
                                    className="mt-0.5"
                                    name="device-wizard-publish"
                                    disabled={disabled}
                                    checked={!!payload.device.publish === val}
                                    onChange={() => patch({ publish: val })}
                                />
                                <span>
                                    {label}
                                    <span className="block text-[11px] text-slate-400">
                                        {disabled
                                            ? '你没有设备发布权限 (device:publish), 需要发布请在工单说明里注明'
                                            : hint}
                                    </span>
                                </span>
                            </label>
                        );
                    })}
                </div>
            </div>

            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold">预检报告</div>
                {checking && <div className="text-xs text-slate-400">预检中…</div>}
                {!checking && !report && <div className="text-xs text-slate-400">尚未预检</div>}
                {report && (
                    <>
                        <div
                            className={`text-xs rounded px-3 py-2 ${
                                report.ok
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : 'bg-rose-50 text-rose-700'
                            }`}
                        >
                            {report.ok ? '✔ 没有阻断错误' : '✖ 存在阻断错误, 修完才能提交'} ·
                            错误 {report.errors.length} · 告警 {report.warnings.length} · 提示{' '}
                            {report.infos.length}
                        </div>
                        <Group
                            title="错误"
                            tone="text-rose-600"
                            issues={report.errors}
                            onGoto={onGoto}
                        />
                        <Group
                            title="告警"
                            tone="text-amber-600"
                            issues={report.warnings}
                            onGoto={onGoto}
                        />
                        <Group title="提示" tone="text-slate-500" issues={report.infos} />
                        {report.warnings.length > 0 && (
                            <label className="flex items-center gap-2 text-xs cursor-pointer border-t border-slate-100 pt-3">
                                <input
                                    type="checkbox"
                                    checked={ack}
                                    onChange={(e) => setAck(e.target.checked)}
                                />
                                我已知晓上述 {report.warnings.length} 条告警, 确认提交
                            </label>
                        )}
                    </>
                )}
            </div>
        </>
    );
}

export default StepDeviceReview;
